/**
 * SHEIN 发品商品图校验与压缩。
 * 官方主图/细节图（image_type 1/2）：
 * - 1340×1785；或宽高比 3:4 且宽高像素均在 900–2200
 * - 站内统一：比例须 3:4；宽高均 ≥900（过小拒绝）；>2200 则缩放到 1340×1785
 * - 格式 JPG / JPEG / PNG，≤3MB
 */

import {
  ASPECT_RATIO_TOLERANCE,
  PRODUCT_CAROUSEL_ASPECT,
  isAspectRatioMatch,
} from '@/components/image/fixedAspectCrop';

export const SHEIN_PRODUCT_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
export const SHEIN_PRODUCT_IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png';
export const SHEIN_PRODUCT_IMAGE_HINT =
  '3:4，宽高 900–2200（推荐 1340×1785）· 不合规将弹裁剪框 · JPG/PNG · ≤3MB';

const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);
const ALLOWED_EXT = new Set(['jpg', 'jpeg', 'png']);
/** 比例容差（浮点像素取整） */
const RATIO_TOLERANCE = ASPECT_RATIO_TOLERANCE;

function fileExt(name) {
  const base = String(name || '');
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

/** @returns {string|null} 错误文案 */
export function validateSheinProductImageFormat(file) {
  if (!file) return '请选择图片文件';
  const mime = String(file.type || '').trim().toLowerCase();
  const ext = fileExt(file.name);
  if (mime && !ALLOWED_MIME.has(mime)) return '仅支持 JPG / JPEG / PNG 格式';
  if (ext && !ALLOWED_EXT.has(ext)) return '仅支持 JPG / JPEG / PNG 格式';
  if (!mime && !ext) return '仅支持 JPG / JPEG / PNG 格式';
  return null;
}

export const SHEIN_MAIN_WIDTH = 1340;
export const SHEIN_MAIN_HEIGHT = 1785;
/** 官方主图/细节图：3:4 时宽高像素区间 */
export const SHEIN_MAIN_DETAIL_MIN = 900;
export const SHEIN_MAIN_DETAIL_MAX = 2200;
export const SHEIN_SQUARE_MIN = 900;
export const SHEIN_SQUARE_MAX = 2200;

/**
 * 站内主图/细节图：
 * - 比例必须 3:4
 * - 宽高均 ≥900（官方 900–2200；过大由 prepare 缩到 1340×1785）
 * @returns {string|null}
 */
export function validateSheinProductImageDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) {
    return '图片尺寸不符合平台要求：须为 3:4，且宽高均在 900–2200px';
  }
  if (!isAspectRatioMatch(w, h, PRODUCT_CAROUSEL_ASPECT, RATIO_TOLERANCE)) {
    return '图片尺寸不符合平台要求：主图/细节图比例须为 3:4';
  }
  if (w < SHEIN_MAIN_DETAIL_MIN || h < SHEIN_MAIN_DETAIL_MIN) {
    return `图片过小：宽高均须 ≥${SHEIN_MAIN_DETAIL_MIN}px（当前 ${w}×${h}）`;
  }
  return null;
}

/**
 * 主图/细节图绘制目标：统一输出 1340×1785（3:4）。
 * 已是 3:4 则整图缩放；否则居中取 3:4 区域（上传 UI 应先弹裁剪框）。
 */
export function resolveSheinMainDetailDraw(width, height) {
  const w = Math.max(1, Number(width) || 0);
  const h = Math.max(1, Number(height) || 0);
  const targetRatio = SHEIN_MAIN_WIDTH / SHEIN_MAIN_HEIGHT;
  const current = w / h;
  let sx = 0;
  let sy = 0;
  let sw = w;
  let sh = h;
  if (Math.abs(current - targetRatio) > RATIO_TOLERANCE) {
    if (current > targetRatio) {
      sh = h;
      sw = Math.max(1, Math.round(h * targetRatio));
      sx = Math.max(0, Math.floor((w - sw) / 2));
    } else {
      sw = w;
      sh = Math.max(1, Math.round(w / targetRatio));
      sy = Math.max(0, Math.floor((h - sh) / 2));
    }
  }
  return { sx, sy, sw, sh, dw: SHEIN_MAIN_WIDTH, dh: SHEIN_MAIN_HEIGHT };
}

/**
 * 在保持像素不变的前提下压缩到 ≤ maxBytes（默认 3MB）。
 * 超出时逐步降低 JPEG 质量；无法压到限制则抛错。
 */
export async function compressSheinProductImageToLimit(file, maxBytes = SHEIN_PRODUCT_IMAGE_MAX_BYTES) {
  if (!file) throw new Error('无效图片');
  if (file.size <= maxBytes) return file;

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法压缩图片');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const baseName = (file.name || 'product').replace(/\.[^.]+$/, '') || 'product';
  let quality = 0.92;
  let best = null;

  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob) {
      best = blob;
      if (blob.size <= maxBytes) {
        return new File([blob], `${baseName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }
    }
    quality -= 0.07;
  }

  if (best && best.size < file.size) {
    // 已尽量压缩但仍超限
    throw new Error('图片压缩后仍超过 3MB，请换一张更小的图');
  }
  throw new Error('图片超过 3MB 且无法有效压缩，请换一张更小的图');
}

/**
 * 由主图 File 居中裁成 1:1 方块图，边长落在 900–2200，体积 ≤3MB。
 * 不依赖远程 URL，避免跨域污染 canvas。
 */
export async function cropFileToSheinSquare(file) {
  if (!file) throw new Error('无效主图');
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取主图尺寸');

  const cropSide = Math.min(width, height);
  const sx = Math.floor((width - cropSide) / 2);
  const sy = Math.floor((height - cropSide) / 2);

  let outSide = cropSide;
  if (outSide < 900) outSide = 900;
  if (outSide > 2200) outSide = 2200;

  const canvas = document.createElement('canvas');
  canvas.width = outSide;
  canvas.height = outSide;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法生成方块图');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outSide, outSide);
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, outSide, outSide);

  const baseName = (file.name || 'square').replace(/\.[^.]+$/, '') || 'square';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= SHEIN_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-square.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('方块图压缩后仍超过 3MB');
}

/** 色块图输出边长（平台要求 80×80） */
export const SHEIN_COLOR_BLOCK_SIDE = 80;
/**
 * 从主图短边取中心区域的比例（局部色块，非整张商品）。
 * 过小易噪点，过大会带入轮廓；约 28% 贴近商家后台「截取局部色块」习惯。
 */
export const SHEIN_COLOR_BLOCK_CENTER_RATIO = 0.28;

/**
 * 色块图（image_type=6）：从主图正中裁出局部正方形色块，缩放到 80×80 JPG。
 * 用于搜索/列表色块展示；上传时走 upload-pic type=6 换 SHEIN URL。
 */
export async function cropFileToSheinColorBlock(file) {
  if (!file) throw new Error('无效主图');
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取主图尺寸');

  const minSide = Math.min(width, height);
  // 至少取 80px 源区域，避免极小图放大发糊
  const cropSide = Math.max(
    SHEIN_COLOR_BLOCK_SIDE,
    Math.floor(minSide * SHEIN_COLOR_BLOCK_CENTER_RATIO),
  );
  const sx = Math.floor((width - cropSide) / 2);
  const sy = Math.floor((height - cropSide) / 2);

  const out = SHEIN_COLOR_BLOCK_SIDE;
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法生成色块图');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out, out);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, out, out);

  const baseName = (file.name || 'color-block').replace(/\.[^.]+$/, '') || 'color-block';
  let quality = 0.92;
  while (quality >= 0.5) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= SHEIN_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-color-block.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('色块图生成失败');
}

/**
 * 校验格式；要求已是 3:4（否则抛错，由上传层弹裁剪框）；
 * 缩放到 1340×1785 并压缩到 ≤3MB。
 */
export async function prepareSheinProductImage(file, { requireAspect = true } = {}) {
  const formatError = validateSheinProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error('无法读取图片尺寸');

  if (requireAspect) {
    const dimError = validateSheinProductImageDimensions(width, height);
    if (dimError) {
      const err = new Error(dimError);
      err.code = 'NEED_ASPECT_CROP';
      throw err;
    }
  }

  const draw = resolveSheinMainDetailDraw(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = draw.dw;
  canvas.height = draw.dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法处理图片');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, draw.dw, draw.dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, draw.sx, draw.sy, draw.sw, draw.sh, 0, 0, draw.dw, draw.dh);

  const baseName = (file.name || 'product').replace(/\.[^.]+$/, '') || 'product';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= SHEIN_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error('图片压缩后仍超过 3MB，请换一张更小的图');
}
